import { useEffect, useRef, useState } from 'react';

import { ApiError } from '../../../api/client';
import { draftPreview } from '../api';
import type { Layout } from './layout';

interface Input {
  layout: Layout;
  media_width_mm: number;
  media_height_mm: number;
  dpi: number;
  entityKind: string;
  entityId?: string | null;
}

interface Preview {
  /** Object URL of the last raster that rendered. Held across a failed render
   *  so the picture does not blink out while a block is mid-drag. */
  src: string | null;
  error: string | null;
  pending: boolean;
}

/**
 * The label as the printer will burn it, refreshed as the layout is edited.
 *
 * Rendered by the server, not drawn in the browser. The canvas beside it is a
 * handle — approximately right, made of `div`s — and this is the answer: at 96
 * dots across a 12 mm tape, whether a name fits is decided by hinting and
 * thresholding inside Pillow, and no amount of CSS predicts it. Every layout
 * defect this project has had was of exactly that kind.
 *
 * Debounced, because dragging a block produces a frame every few milliseconds
 * and each render is a real rasterisation on the server. Three hundred
 * milliseconds is about the pause that follows letting go.
 *
 * A failed render keeps the previous picture and shows the reason underneath.
 * Clearing the image would take away the thing being compared against at the
 * very moment the person needs it — and half of these failures are transient:
 * a block dragged over the edge on its way somewhere legal.
 */
export function useDraftPreview(input: Input, delay = 300): Preview {
  const [state, setState] = useState<Preview>({ src: null, error: null, pending: true });
  const url = useRef<string | null>(null);

  // Serialised, so the effect re-runs when the layout changes rather than on
  // every render: `layout` is rebuilt by every keystroke in the form, and a
  // reference dependency would request a raster for each of them.
  const key = JSON.stringify(input);

  useEffect(() => {
    let cancelled = false;
    setState((current) => ({ ...current, pending: true }));

    const timer = window.setTimeout(() => {
      const request: Input = JSON.parse(key) as Input;
      void draftPreview({
        layout: request.layout,
        media_width_mm: request.media_width_mm,
        media_height_mm: request.media_height_mm,
        dpi: request.dpi,
        entity_kind: request.entityKind,
        entity_id: request.entityId ?? null,
      })
        .then((blob) => {
          if (cancelled) return;
          const next = URL.createObjectURL(blob);
          if (url.current) URL.revokeObjectURL(url.current);
          url.current = next;
          setState({ src: next, error: null, pending: false });
        })
        .catch((exc: unknown) => {
          if (cancelled) return;
          setState((current) => ({
            ...current,
            error: exc instanceof ApiError ? exc.code : 'unknown',
            pending: false,
          }));
        });
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [key, delay]);

  // Released only when the screen closes: the intermediate ones are revoked as
  // they are replaced above, and a hundred rasters held for the length of an
  // editing session is a hundred images of memory.
  useEffect(
    () => () => {
      if (url.current) URL.revokeObjectURL(url.current);
    },
    [],
  );

  return state;
}
